import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import i18n from '../i18n'
import { localizeApiDetail } from '../i18n/apiError'
import { publicAPI, AvgAgeByMasterModelRow } from '../services/api'
import LoadingSpinner from './LoadingSpinner'
import ErrorMessage from './ErrorMessage'
import ExportButtons from './ExportButtons'
import { ColumnDefinition } from '../utils/exportToCsv'
import { ChevronRight, ChevronLeft, Search } from 'lucide-react'

interface MasterModelGridProps {
  outputMode: string
  category: string
  varebil: boolean | undefined
  minePostnummer: string | undefined
  customStartDate: string
  customEndDate: string
  activeTab: string
}

interface PeriodInfo {
  label: string
  start_date: string
  end_date: string
}

interface PeriodsInfo {
  current: PeriodInfo
  previous: PeriodInfo
}

const PAGE_SIZE = 50

const toNumber = (v: number | string | null | undefined): number => {
  if (v === null || v === undefined) return 0
  return typeof v === 'string' ? parseFloat(v) : v
}

export default function MasterModelGrid({
  outputMode,
  category,
  varebil,
  minePostnummer,
  customStartDate,
  customEndDate,
  activeTab,
}: MasterModelGridProps) {
  const { t } = useTranslation('components')
  const navigate = useNavigate()

  const [data, setData] = useState<AvgAgeByMasterModelRow[]>([])
  const [summary, setSummary] = useState<AvgAgeByMasterModelRow | null>(null)
  const [totalRows, setTotalRows] = useState(0)
  const [page, setPage] = useState(0)
  const [search, setSearch] = useState('')
  const [pendingSearch, setPendingSearch] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [periods, setPeriods] = useState<PeriodsInfo | null>(null)

  const fetchData = useCallback(async (pageNum: number, searchTerm: string) => {
    try {
      setLoading(true)
      setError(null)
      const params: Record<string, any> = {
        output_mode: outputMode,
        category,
        limit: PAGE_SIZE,
        offset: pageNum * PAGE_SIZE,
      }
      if (varebil !== undefined) params.varebil = varebil
      if (minePostnummer) params.mine_postnummer = minePostnummer
      if (outputMode === 'custom' && customStartDate && customEndDate) {
        params.custom_start_date = customStartDate
        params.custom_end_date = customEndDate
      }
      if (searchTerm) params.search = searchTerm

      const res = await publicAPI.getAvgAgeByMasterModel(params)
      const responseData = res.data
      const allRows: AvgAgeByMasterModelRow[] = responseData.data || []

      // First row with brand === 'TOTAL' is the summary
      const summaryRow = allRows.find(r => r.brand === 'TOTAL')
      const dataRows = allRows.filter(r => r.brand !== 'TOTAL')

      setSummary(summaryRow || null)
      setData(dataRows)
      setTotalRows(responseData.total_rows || 0)
      if (responseData.periods) setPeriods(responseData.periods)
    } catch (err: any) {
      setError(localizeApiDetail(err.response?.data?.detail) || err.message || i18n.t('components:masterModelGrid.loadError'))
    } finally {
      setLoading(false)
    }
  }, [outputMode, category, varebil, minePostnummer, customStartDate, customEndDate])

  // Fetch on mount and when filters change
  useEffect(() => {
    setPage(0)
    setSearch('')
    setPendingSearch('')
    fetchData(0, '')
  }, [outputMode, category, varebil, minePostnummer, customStartDate, customEndDate, fetchData])

  // Fetch when page changes
  useEffect(() => {
    fetchData(page, search)
  }, [page]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearch = () => {
    setPage(0)
    setSearch(pendingSearch)
    fetchData(0, pendingSearch)
  }

  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch()
  }

  const ChangeIndicator = ({ current, previous }: { current: number | null; previous: number | null }) => {
    if (current === null || previous === null) return <span className="text-gray-400">-</span>
    const diff = current - previous
    if (Math.abs(diff) < 0.05) return <span className="text-gray-500">0,0</span>
    const colorClass = diff > 0
      ? 'text-green-600 dark:text-green-400'
      : 'text-red-600 dark:text-red-400'
    return (
      <span className={colorClass}>
        {diff.toLocaleString('nb-NO', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}
      </span>
    )
  }

  const startRow = page * PAGE_SIZE + 1
  const endRow = Math.min((page + 1) * PAGE_SIZE, totalRows)
  const totalPages = Math.ceil(totalRows / PAGE_SIZE)

  const exportColumns: ColumnDefinition[] = [
    { key: 'rank', label: '#' },
    { key: 'brand', label: t('masterModelGrid.brand') },
    { key: 'master_model_name', label: t('masterModelGrid.model') },
    { key: 'current_count', label: periods?.current?.label || t('masterModelGrid.now'), format: (v) => toNumber(v).toLocaleString('nb-NO') },
    { key: 'current_avg_age', label: t('masterModelGrid.avgAge'), format: (v) => v !== null ? toNumber(v).toLocaleString('nb-NO', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) : '-' },
    { key: 'previous_count', label: periods?.previous?.label || t('masterModelGrid.previous'), format: (v) => toNumber(v).toLocaleString('nb-NO') },
    { key: 'previous_avg_age', label: t('masterModelGrid.avgAgePrevious'), format: (v) => v !== null ? toNumber(v).toLocaleString('nb-NO', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) : '-' },
  ]

  return (
    <div className="card p-6">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
          {t('masterModelGrid.title')}
        </h2>
        <ExportButtons
          data={data.map((row, idx) => ({ rank: startRow + idx, ...row }))}
          columns={exportColumns}
          metadata={{
            title: t('exportTitles.ageMasterModel', { mode: outputMode }),
            dateRange: periods ? t('masterModelGrid.dateRange', { start: periods.current.start_date, end: periods.current.end_date }) : '',
          }}
          disabled={loading}
        />
      </div>

      {/* Search bar */}
      <div className="mb-4 flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            value={pendingSearch}
            onChange={e => setPendingSearch(e.target.value)}
            onKeyDown={handleSearchKeyDown}
            placeholder={t('masterModelGrid.searchPlaceholder')}
            className="input-field pl-10"
          />
        </div>
        <button onClick={handleSearch} className="btn-primary text-sm">
          {t('masterModelGrid.search')}
        </button>
      </div>

      {loading ? (
        <LoadingSpinner />
      ) : error ? (
        <ErrorMessage message={error} />
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="data-table data-table-compact">
              <thead>
                <tr>
                  <th className="text-center w-12">#</th>
                  <th className="text-left">{t('masterModelGrid.brand')}</th>
                  <th className="text-left">{t('masterModelGrid.model')}</th>
                  <th className="text-right">{periods?.current?.label || t('masterModelGrid.now')}</th>
                  <th className="text-right border-r-2 border-gray-300 dark:border-gray-600">{t('masterModelGrid.avgAge')}</th>
                  <th className="text-right">{periods?.previous?.label || t('masterModelGrid.previous')}</th>
                  <th className="text-right border-r-2 border-gray-300 dark:border-gray-600">{t('masterModelGrid.avgAge')}</th>
                  <th className="text-right">{t('masterModelGrid.ageChange')}</th>
                  <th className="text-center">{t('masterModelGrid.details')}</th>
                </tr>
              </thead>
              <tbody>
                {/* TOTAL summary row - pinned at top */}
                {summary && (
                  <tr className="bg-primary-50 dark:bg-primary-900/20 font-bold border-t border-b border-primary-300 dark:border-primary-600">
                    <td className="text-center py-2">
                      <span className="text-primary-600 dark:text-primary-400">&#8721;</span>
                    </td>
                    <td className="font-bold text-primary-700 dark:text-primary-300 py-2" colSpan={2}>TOTAL</td>
                    <td className="text-right font-bold py-2">{toNumber(summary.current_count).toLocaleString('nb-NO')}</td>
                    <td className="text-right border-r-2 border-gray-300 dark:border-gray-600 py-2">
                      <span className="font-bold text-gray-900 dark:text-gray-100">
                        {summary.current_avg_age !== null ? summary.current_avg_age.toLocaleString('nb-NO', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) : '-'}
                      </span>
                    </td>
                    <td className="text-right font-bold py-2">{toNumber(summary.previous_count).toLocaleString('nb-NO')}</td>
                    <td className="text-right border-r-2 border-gray-300 dark:border-gray-600 py-2">
                      <span className="font-bold text-gray-900 dark:text-gray-100">
                        {summary.previous_avg_age !== null ? summary.previous_avg_age.toLocaleString('nb-NO', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) : '-'}
                      </span>
                    </td>
                    <td className="text-right font-bold py-2">
                      <ChangeIndicator current={summary.current_avg_age} previous={summary.previous_avg_age} />
                    </td>
                    <td className="text-center py-2">
                      <span className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">&mdash;</span>
                    </td>
                  </tr>
                )}
                {data.map((row, idx) => (
                  <tr
                    key={row.master_model_id}
                    className={`hover:bg-gray-100 dark:hover:bg-dark-border transition-colors cursor-pointer ${idx % 2 === 0 ? 'bg-gray-50 dark:bg-gray-800/30' : ''}`}
                    onClick={() => navigate(`/age-analytics/model/${row.master_model_id}?tab=${activeTab}&mode=${outputMode}`)}
                  >
                    <td className="text-center text-gray-700 dark:text-gray-300">{startRow + idx}</td>
                    <td className="font-medium">{row.brand}</td>
                    <td className="font-medium">{row.master_model_name}</td>
                    <td className="text-right">{toNumber(row.current_count).toLocaleString('nb-NO')}</td>
                    <td className="text-right border-r-2 border-gray-300 dark:border-gray-600">
                      <span className="text-gray-900 dark:text-gray-100">
                        {row.current_avg_age !== null ? row.current_avg_age.toLocaleString('nb-NO', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) : '-'}
                      </span>
                    </td>
                    <td className="text-right">{toNumber(row.previous_count).toLocaleString('nb-NO')}</td>
                    <td className="text-right border-r-2 border-gray-300 dark:border-gray-600">
                      <span className="text-gray-900 dark:text-gray-100">
                        {row.previous_avg_age !== null ? row.previous_avg_age.toLocaleString('nb-NO', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) : '-'}
                      </span>
                    </td>
                    <td className="text-right">
                      <ChangeIndicator current={row.current_avg_age} previous={row.previous_avg_age} />
                    </td>
                    <td className="text-center">
                      <button
                        onClick={e => { e.stopPropagation(); navigate(`/age-analytics/model/${row.master_model_id}?tab=${activeTab}&mode=${outputMode}`) }}
                        className="inline-flex items-center gap-1 text-primary-600 dark:text-primary-400 hover:underline"
                      >
                        <span className="text-sm font-medium">{t('masterModelGrid.details')}</span>
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {data.length === 0 && (
              <p className="text-center text-gray-500 dark:text-gray-400 py-8">{t('masterModelGrid.noData')}</p>
            )}
          </div>

          {/* Pagination */}
          {totalRows > 0 && (
            <div className="flex justify-between items-center mt-4 pt-4 border-t border-gray-200 dark:border-dark-border">
              <span className="text-sm text-gray-600 dark:text-gray-400">
                {t('masterModelGrid.showing', { from: startRow, to: endRow, total: totalRows.toLocaleString('nb-NO') })}
              </span>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage(p => Math.max(0, p - 1))}
                  disabled={page === 0}
                  className="btn-secondary text-sm flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="w-4 h-4" />
                  {t('masterModelGrid.previous')}
                </button>
                <button
                  onClick={() => setPage(p => p + 1)}
                  disabled={page >= totalPages - 1}
                  className="btn-secondary text-sm flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t('masterModelGrid.next')}
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}
